import {
  AlignLeft,
  BarChart3,
  ChartColumn,
  ChartLine,
  ChartPie,
  Hash,
  List,
  Rows3,
  Table2,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { WIDGET_VIEW_LABELS, type WidgetView } from '@/lib/dashboardWidgets'
import { shortDayLabel } from '@/lib/dashboardWidgetFormat'
import { cn } from '@/lib/utils'
import { StatusBar, STATUS_COLORS } from './charts'

// ─── Switchable representations (z8nrz7buqm) ────────────────────────────────
//
// Two shapes cover every widget's data: PARTS of a whole (counts by type, by
// status, by stage) and a SERIES over days. Each can be drawn several ways;
// the widget offers only the ways its data suits (`widgetViews`). Built to the
// data-viz rules this app follows: fixed categorical order, at most five donut
// slices (the rest fold into "Other"), a legend whenever colour carries
// identity, values in text colours, a tooltip on every mark.

const VIEW_ICONS: Record<WidgetView, LucideIcon> = {
  number: Hash,
  trend: TrendingUp,
  columns: ChartColumn,
  lines: ChartLine,
  bars: BarChart3,
  donut: ChartPie,
  stack: Rows3,
  stages: AlignLeft,
  list: List,
  table: Table2,
}

/**
 * The icon toggle in a widget's header: one button per view it offers.
 *
 * Click shows a view for now; double-click makes it the widget's default, the
 * one it opens in every time (a dot under the icon marks it). Without
 * `onMakeDefault` (while editing a layout) a click is all there is, and it goes
 * into the draft like every other edit.
 */
export function ViewSwitcher({
  views,
  value,
  defaultView,
  onChange,
  onMakeDefault,
  title,
}: {
  views: WidgetView[]
  value: WidgetView
  defaultView: WidgetView
  onChange: (view: WidgetView) => void
  onMakeDefault?: (view: WidgetView) => void
  title: string
}) {
  if (views.length < 2) return null
  return (
    <div role='group' aria-label={`Show ${title} as`} className='flex shrink-0 rounded-md bg-gray-100 p-0.5'>
      {views.map(v => {
        const Icon = VIEW_ICONS[v]
        const isDefault = v === defaultView
        const hint = onMakeDefault
          ? isDefault
            ? `${WIDGET_VIEW_LABELS[v]} (default)`
            : `${WIDGET_VIEW_LABELS[v]} · double-click to make default`
          : WIDGET_VIEW_LABELS[v]
        return (
          <button
            key={v}
            type='button'
            aria-pressed={value === v}
            aria-label={hint}
            title={hint}
            onClick={() => onChange(v)}
            onDoubleClick={onMakeDefault && (() => onMakeDefault(v))}
            className={cn(
              'relative rounded p-1 transition-colors',
              value === v ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-400 hover:text-gray-600',
            )}
          >
            <Icon className='h-3.5 w-3.5' />
            {onMakeDefault && isDefault && (
              <span className='absolute bottom-0 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-blue-600' aria-hidden />
            )}
          </button>
        )
      })}
    </div>
  )
}

const PARTS_KINDS = ['donut', 'bars', 'stack', 'table'] as const
const SERIES_KINDS = ['trend', 'columns', 'lines', 'table'] as const

/** The PartsView kind for a widget view, or `fallback` for a view that is not a parts kind. */
export function partsKind(view: WidgetView, fallback: (typeof PARTS_KINDS)[number] = 'bars') {
  return (PARTS_KINDS as readonly string[]).includes(view) ? (view as (typeof PARTS_KINDS)[number]) : fallback
}

/** The SeriesView kind for a widget view, or `fallback`. */
export function seriesKind(view: WidgetView, fallback: (typeof SERIES_KINDS)[number] = 'trend') {
  return (SERIES_KINDS as readonly string[]).includes(view) ? (view as (typeof SERIES_KINDS)[number]) : fallback
}

/** Fixed categorical order, the validated procedure-type set; never cycled. */
export const CATEGORICAL = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4'] as const
const OTHER = '#9ca3af'

export type Part = { label: string; value: number; color?: string }

const TOOLTIP_STYLE = { fontSize: 12, borderRadius: 8, padding: '4px 10px' }

function withColors(parts: Part[]): Required<Part>[] {
  return parts.map((p, i) => ({ ...p, color: p.color ?? CATEGORICAL[i % CATEGORICAL.length] }))
}

/** At most five slices: the smallest beyond four fold into one "Other". */
function foldForDonut(parts: Required<Part>[]): Required<Part>[] {
  if (parts.length <= 5) return parts
  const sorted = [...parts].sort((a, b) => b.value - a.value)
  const rest = sorted.slice(4).reduce((n, p) => n + p.value, 0)
  return [...sorted.slice(0, 4), { label: 'Other', value: rest, color: OTHER }]
}

/**
 * Parts of one whole, as a donut (total in the middle), horizontal bars, one
 * stacked bar, or a table. Every view names each part with its number, so no
 * part is told apart by colour alone.
 */
export function PartsView({
  view,
  parts: raw,
  totalLabel = 'Total',
}: {
  view: 'donut' | 'bars' | 'stack' | 'table'
  parts: Part[]
  totalLabel?: string
}) {
  const parts = withColors(raw)
  const total = parts.reduce((n, p) => n + p.value, 0)

  if (view === 'stack') return <StatusBar segments={parts} />

  if (view === 'table') {
    return (
      <table className='w-full text-xs'>
        <thead>
          <tr className='border-b border-gray-100 text-left text-[11px] text-gray-400'>
            <th className='py-1 font-medium'>{totalLabel === 'Total' ? 'Item' : totalLabel}</th>
            <th className='py-1 text-right font-medium'>Count</th>
            <th className='py-1 text-right font-medium'>Share</th>
          </tr>
        </thead>
        <tbody>
          {parts.map(p => (
            <tr key={p.label} className='border-b border-gray-50 last:border-0'>
              <td className='py-1.5'>
                <span className='mr-1.5 inline-block h-2 w-2 rounded-sm' style={{ backgroundColor: p.color }} aria-hidden />
                <span className='text-gray-700'>{p.label}</span>
              </td>
              <td className='py-1.5 text-right font-semibold tabular-nums text-gray-900'>{p.value}</td>
              <td className='py-1.5 text-right tabular-nums text-gray-500'>
                {total > 0 ? `${Math.round((p.value / total) * 100)}%` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )
  }

  if (view === 'bars') {
    return (
      <ResponsiveContainer width='100%' height={Math.max(96, parts.length * 30)}>
        <BarChart data={parts} layout='vertical' margin={{ top: 0, right: 28, bottom: 0, left: 0 }} barCategoryGap={6}>
          <XAxis type='number' allowDecimals={false} hide />
          <YAxis
            type='category'
            dataKey='label'
            width={104}
            tick={{ fontSize: 11, fill: '#6b7280' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip cursor={{ fill: '#f3f4f6' }} contentStyle={TOOLTIP_STYLE} formatter={v => [v as number, 'Count']} />
          <Bar
            dataKey='value'
            radius={[0, 4, 4, 0]}
            maxBarSize={22}
            isAnimationActive={false}
            label={{ position: 'right', fontSize: 11, fill: '#374151' }}
          >
            {parts.map(p => (
              <Cell key={p.label} fill={p.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    )
  }

  // Donut
  const slices = foldForDonut(parts).filter(p => p.value > 0)
  return (
    <div className='flex flex-1 items-center gap-4'>
      <div className='relative h-[120px] w-[120px] shrink-0'>
        <ResponsiveContainer width='100%' height='100%'>
          <PieChart>
            <Pie
              data={slices.length > 0 ? slices : [{ label: 'None', value: 1, color: '#f3f4f6' }]}
              dataKey='value'
              nameKey='label'
              innerRadius={38}
              outerRadius={56}
              paddingAngle={slices.length > 1 ? 2 : 0}
              stroke='#fff'
              strokeWidth={2}
              isAnimationActive={false}
            >
              {(slices.length > 0 ? slices : [{ label: 'None', color: '#f3f4f6' }]).map(p => (
                <Cell key={p.label} fill={p.color} />
              ))}
            </Pie>
            {slices.length > 0 && <Tooltip contentStyle={TOOLTIP_STYLE} />}
          </PieChart>
        </ResponsiveContainer>
        <div className='pointer-events-none absolute inset-0 flex flex-col items-center justify-center'>
          <span className='text-lg font-bold tabular-nums text-gray-900'>{total}</span>
          <span className='text-[10px] text-gray-400'>{totalLabel}</span>
        </div>
      </div>
      <ul className='min-w-0 flex-1 space-y-1.5'>
        {foldForDonut(parts).map(p => (
          <li key={p.label} className='flex items-center justify-between gap-2 text-xs'>
            <span className='flex min-w-0 items-center gap-1.5'>
              <span className='h-2.5 w-2.5 shrink-0 rounded-sm' style={{ backgroundColor: p.color }} aria-hidden />
              <span className='truncate text-gray-600'>{p.label}</span>
            </span>
            <span className='shrink-0 font-semibold tabular-nums text-gray-800'>
              {p.value}
              {total > 0 && <span className='ml-1 font-normal text-gray-400'>{Math.round((p.value / total) * 100)}%</span>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export type SeriesDef = { key: string; label: string; color: string }

/**
 * A series over days, as a filled trend, columns, lines (several series) or a
 * day-by-day table. One series needs no legend (the widget title names it);
 * two or more always get one.
 */
export function SeriesView({
  view,
  rows,
  series,
  height = 150,
}: {
  view: 'trend' | 'columns' | 'lines' | 'table'
  rows: ({ day: string } & Record<string, number | string>)[]
  series: SeriesDef[]
  height?: number
}) {
  const data: Record<string, number | string>[] = rows.map(r => ({ ...r, label: shortDayLabel(r.day) }))
  const legend = series.length > 1

  if (view === 'table') {
    const shown = [...data].reverse().filter(r => series.some(s => Number(r[s.key]) > 0))
    return (
      <div className='max-h-[220px] overflow-y-auto'>
        <table className='w-full text-xs'>
          <thead className='sticky top-0 bg-white'>
            <tr className='border-b border-gray-100 text-left text-[11px] text-gray-400'>
              <th className='py-1 font-medium'>Day</th>
              {series.map(s => (
                <th key={s.key} className='py-1 text-right font-medium'>
                  {s.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr>
                <td colSpan={series.length + 1} className='py-4 text-center text-gray-400'>
                  Nothing in this period.
                </td>
              </tr>
            )}
            {shown.map(r => (
              <tr key={String(r.day)} className='border-b border-gray-50 last:border-0'>
                <td className='py-1.5 text-gray-600'>{r.label}</td>
                {series.map(s => (
                  <td key={s.key} className='py-1.5 text-right font-semibold tabular-nums text-gray-900'>
                    {r[s.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  const axes = (
    <>
      <CartesianGrid vertical={false} stroke='#f3f4f6' />
      <XAxis
        dataKey='label'
        tick={{ fontSize: 10, fill: '#9ca3af' }}
        axisLine={false}
        tickLine={false}
        interval='preserveStartEnd'
        minTickGap={20}
      />
      <YAxis allowDecimals={false} width={28} tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
      <Tooltip contentStyle={TOOLTIP_STYLE} cursor={view === 'columns' ? { fill: '#f3f4f6' } : { stroke: '#e5e7eb' }} />
      {legend && <Legend verticalAlign='top' align='right' iconSize={10} wrapperStyle={{ fontSize: 11, top: -4 }} />}
    </>
  )
  const margin = { top: legend ? 16 : 6, right: 8, bottom: 0, left: -8 }

  return (
    <ResponsiveContainer width='100%' height={height}>
      {view === 'columns' ? (
        <BarChart data={data} margin={margin} barCategoryGap={2}>
          {axes}
          {series.map(s => (
            <Bar key={s.key} dataKey={s.key} name={s.label} fill={s.color} radius={[4, 4, 0, 0]} maxBarSize={24} isAnimationActive={false} />
          ))}
        </BarChart>
      ) : view === 'trend' ? (
        <AreaChart data={data} margin={margin}>
          {axes}
          {series.map(s => (
            <Area
              key={s.key}
              type='monotone'
              dataKey={s.key}
              name={s.label}
              stroke={s.color}
              strokeWidth={2}
              fill={s.color}
              fillOpacity={0.1}
              activeDot={{ r: 4, stroke: '#fff', strokeWidth: 2 }}
              isAnimationActive={false}
            />
          ))}
        </AreaChart>
      ) : (
        <LineChart data={data} margin={margin}>
          {axes}
          {series.map(s => (
            <Line
              key={s.key}
              type='monotone'
              dataKey={s.key}
              name={s.label}
              stroke={s.color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, stroke: '#fff', strokeWidth: 2 }}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      )}
    </ResponsiveContainer>
  )
}

/** A day-count series as SeriesView rows. */
export function dailyRows(points: { day: string; count: number }[], key = 'count') {
  return points.map(p => ({ day: p.day, [key]: p.count }))
}

export { STATUS_COLORS }
